import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import { IDetailedProject, IRequirement } from "../index";
import AddActivity from "./AddActivity";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import ProjectTypes from "../../../enums/ProjectTypes";
import { ContainerAction, ContainerCards, ContentCards } from "./styles";
import { useAuth } from "../../../hooks/auth";
import UserRoles from "../../../enums/UserRoles";

const { Text } = Typography;

interface IActivityAllTimeModalProps {
  visible: boolean;
  project: IDetailedProject;
  handleCloseModal: (requirements?: IRequirement[]) => void;
}

const AddActivityAllTime: React.FC<IActivityAllTimeModalProps> = ({
  visible,
  project,
  handleCloseModal,
}) => {
  const { t } = useTranslation(["activities"]);
  const [loading, setLoading] = useState(false);
  const [addActivity, setAddActivity] = useState<Partial<IRequirement>[]>([]);
  const [activities, setActivities] = useState<IRequirement[]>(
    project.requirements || [],
  );
  const [closeActivityModal, setCloseActivityModal] = useState(false);
  const [form] = Form.useForm();

  const { addToast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        hours: activities?.reduce((acc, curr) => acc + curr?.hours, 0),
      });
    } else {
      form.resetFields();
      setActivities(project?.requirements || []);
    }
  }, [activities, form, visible, project?.requirements]);

  const isAllocation = useMemo(() => {
    return project.projectType.acronym === ProjectTypes.Allocation;
  }, [project.projectType.acronym]);

  const isAdminOrManager = useMemo(() => {
    if (user.role === UserRoles.Developer) {
      return false;
    }
    return (
      user.role === UserRoles.Administrative || user.role === UserRoles.Manager
    );
  }, [user.role]);

  const hoursActivityOld = useMemo(() => {
    return project?.requirements?.reduce((acc, curr) => acc + curr?.hours, 0);
  }, [project?.requirements]);

  const hoursActivityNew = useMemo(() => {
    return [...activities, ...addActivity]?.reduce(
      (acc, curr) => acc + (curr?.hours || 0),
      0,
    );
  }, [activities, addActivity]);

  const handleClose = useCallback(
    (requirements?: IRequirement[]) => {
      form.resetFields();
      handleCloseModal(requirements);
    },
    [form, handleCloseModal],
  );

  const updateHourActivity = useCallback((value: string, id: string) => {
    setActivities(old =>
      old.map(activity => {
        if (activity.id === id) {
          return { ...activity, hours: Number(value) };
        }
        return activity;
      }),
    );
  }, []);

  const handleCloseActivityModal = useCallback(
    (payload?: Partial<IRequirement>) => {
      if (payload) {
        setAddActivity(old => {
          if (old) {
            return [...old, payload];
          }
          return [payload];
        });
      }
      setCloseActivityModal(false);
    },
    [],
  );

  const confirm = useCallback((ok: () => void, text: string) => {
    Modal.confirm({
      title: "Confirmação",
      content: text,
      okText: "Sim",
      cancelText: "Não",
      onOk: () => ok(),
    });
  }, []);

  const handleCancel = useCallback(() => {
    if (addActivity.length) {
      confirm(() => {
        setAddActivity([]);
        handleClose();
      }, "Deseja cancelar a adição de atividades?");
    } else {
      setAddActivity([]);
      handleClose();
    }
  }, [handleClose, addActivity]);

  const removeActivity = useCallback(
    (id: string) => {
      setAddActivity(old =>
        old.filter(activity => {
          if (activity.id === id) {
            addToast({
              type: "success",
              title: "Atividade pendete removida",
              description: "A atividade pendente foi removida com sucesso",
            });
          }
          return activity.id !== id;
        }),
      );
    },
    [addToast],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdminOrManager) {
        // atualizar atividades que tiveram horas alteradas
        const payload = activities.map(activity => {
          return {
            activity_id: activity.id,
            hours: activity.hours,
          };
        });
        await api.patch("/activities/by_projects/hours", { activity: payload });
      }

      // criar novas atividades
      const promises: any[] = [];
      addActivity.forEach(activity => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = activity;
        promises.push(api.post("/activities", rest));
      });

      const activitiesRequestData = await Promise.all(promises);

      const newActivities = (activitiesRequestData.map(
        act => act.data,
      ) as unknown) as IRequirement[];

      addToast({
        type: "success",
        title: "Atividades atualizadas",
        description: "As atividades foram atualizadas com sucesso",
      });

      handleClose([...activities, ...newActivities]);
      setAddActivity([]);
    } catch (err) {
      addToast({
        type: "error",
        title: "Erro ao atualizar atividades",
        description: "Ocorreu um erro ao atualizar as atividades",
      });
    } finally {
      setLoading(false);
    }
  }, [addActivity, activities, isAdminOrManager]);

  const generateDate = useCallback((year: number, month: number) => {
    const date = new Date(year, month);
    return moment(date).format("MMMM [de] YYYY");
  }, []);

  return (
    <>
      <Modal
        title={t("Atualizar Atividades")}
        visible={visible}
        onOk={() => form.submit()}
        onCancel={() => handleCancel()}
        okButtonProps={{
          htmlType: "submit",
          loading,
        }}
        getContainer={false}
        forceRender
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Text type="warning">
            {t("Total de horas")}: {hoursActivityNew}h / {hoursActivityOld}h
          </Text>
          <ContainerCards
            style={{
              opacity: isAdminOrManager ? 1 : 0.5,
              pointerEvents: isAdminOrManager ? "auto" : "none",
            }}
          >
            <Text type="success" style={{ marginBottom: "5px" }}>
              Atividades Adicionadas
            </Text>
            {activities?.map(activity => (
              <ContentCards key={activity.id}>
                {isAllocation ? (
                  <>
                    <Text>
                      {`${generateDate(activity.year, activity.month)} - `}{" "}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text> {activity.identifier} - </Text>
                    <Text> {activity.description} - </Text>
                  </>
                )}

                <Form.Item
                  name={activity.id}
                  initialValue={activity.hours}
                  noStyle
                >
                  <Input
                    style={{ maxWidth: "100px", width: "100%" }}
                    onChange={
                      value =>
                        updateHourActivity(value.target.value, activity.id)
                      // ts-ignore
                    }
                  />
                </Form.Item>
              </ContentCards>
            ))}
          </ContainerCards>
          <ContainerCards hidden={!addActivity.length}>
            <Text type="danger" style={{ marginBottom: "5px" }}>
              Atividades Pendentes
            </Text>
            {addActivity?.map(activity => (
              <ContentCards
                key={activity.id}
                style={{ justifyContent: "space-between" }}
              >
                <div>
                  {isAllocation ? (
                    <>
                      <Text>
                        {`${generateDate(
                          activity.year || 0,
                          activity.month || 0,
                        )} - `}{" "}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text> {activity.identifier} - </Text>
                      <Text> {activity.description} - </Text>
                    </>
                  )}
                  <Text> {activity.hours}h </Text>
                </div>
                <Button
                  type="link"
                  onClick={
                    () =>
                      confirm(
                        () => removeActivity(activity?.id || ""),
                        "Deseja remover a atividade pendente?",
                      )
                    // ts-ignore
                  }
                >
                  <DeleteOutlined />
                </Button>
              </ContentCards>
            ))}
          </ContainerCards>
          <ContainerAction>
            <Text>Nova Atividade</Text>
            <Button onClick={() => setCloseActivityModal(true)}>
              <PlusOutlined />
            </Button>
          </ContainerAction>
        </Form>
      </Modal>
      <AddActivity
        visible={closeActivityModal}
        projectType={project.projectType.acronym}
        project={project}
        handleCloseModal={handleCloseActivityModal}
      />
    </>
  );
};

export default AddActivityAllTime;
